import { createContext, useContext } from 'react';
import { Node } from '@tiptap/core';
import { NodeViewContent, NodeViewWrapper, ReactNodeViewRenderer, type NodeViewProps } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { PROCEDURE_HEADER_TOP, procedureHeaderReservation } from '@atek/eqms-document-layout';

export interface ProcedureHeaderMetadata {
  title: string;
  controlCode: string;
  department: string;
  effectiveDate: string;
  revision: string;
  logoDataUrl?: string;
  language?: 'en' | 'fr' | 'bilingual';
  localizedMetadata?: Partial<Record<'en' | 'fr', { title: string; department: string }>>;
  layoutVersion?: number;
}

export const ProcedureHeaderContext = createContext<ProcedureHeaderMetadata>({
  title: '', controlCode: '', department: '', effectiveDate: '', revision: '',
});

// Keep source identity and printed fields in the root JSON, not editable body text.
export const AuthoringDocument = Node.create<{ procedure: boolean }>({
  name: 'doc', topNode: true,
  addOptions: () => ({ procedure: false }),
  content() { return this.options.procedure ? 'procedureSection{1,2}' : 'block+'; },
  addAttributes: () => ({
    documentType: { default: null }, templateId: { default: null },
    effectiveDate: { default: null }, revision: { default: null },
    formVersionIds: { default: null }, localizedMetadata: { default: null }, layoutVersion: { default: null },
  }),
});

/** Header metrics are identical to the DMS print header. */
export function DocumentHeader({ metadata, language = 'en', glossary = false }: { metadata: ProcedureHeaderMetadata; language?: 'en' | 'fr'; glossary?: boolean }) {
  const { t } = useTranslation('qms');
  const french = language === 'fr';
  const localized = metadata.localizedMetadata;
  const title = localized ? localized[language]?.title || '' : metadata.title;
  const department = localized ? localized[language]?.department || '' : metadata.department;
  const reservation = glossary ? null : procedureHeaderReservation({ title, department, controlCode: metadata.controlCode, effectiveDate: metadata.effectiveDate, revision: metadata.revision }, language, metadata.layoutVersion === 2);
  return <div data-document-header="" data-header-reservation={reservation ?? undefined} data-header-numbering={metadata.layoutVersion === 2 ? 'global' : 'section'} className={`eqms-document-header${metadata.layoutVersion === 2 ? '' : ' eqms-document-header-legacy'}`} style={reservation == null ? undefined : { height: reservation - 48, margin: 0 }} contentEditable={false} data-testid={glossary ? 'glossary-header' : `procedure-header-${language}`}>
    <div className="eqms-document-header-row" style={reservation == null ? undefined : { transform: `translateY(${PROCEDURE_HEADER_TOP - 48}px)` }}>
      <div className="eqms-document-logo">{metadata.logoDataUrl && <img src={metadata.logoDataUrl} alt="ATEK" draggable={false} />}</div>
      <table aria-label={t(glossary ? 'authoring.glossaryHeader' : 'authoring.procedureHeader')}>
        <colgroup><col style={{ width: french ? '43.24%' : '50.08%' }} /><col style={{ width: french ? '33.56%' : '26.71%' }} /><col style={{ width: '23.21%' }} /></colgroup>
        <tbody><tr>
          <td><strong>{french ? 'Titre:' : 'Title:'}</strong> {title}</td>
          <td><strong>{french ? 'N⁰ de Document:' : 'Document No :'}</strong> {metadata.controlCode || (glossary ? 'XX.XX' : 'XX.XX.XX')}</td>
          <td>Page <span data-header-page="">1</span> {french ? 'de' : 'of'} <span data-header-total="">—</span></td>
        </tr><tr>
          <td><strong>{french ? 'Propriétaire du document:' : 'Document Owner :'}</strong> {department}</td>
          <td><strong>{french ? 'Date mise en vigueur:' : 'Effective Date:'}</strong> {metadata.effectiveDate || 'YYYY-MM-DD'}</td>
          <td><strong>{french ? 'N⁰ de Révision:' : 'Revision No:'}</strong> {metadata.revision || '0'}</td>
        </tr></tbody>
      </table>
    </div>
  </div>;
}

export function GlossaryHeader({ metadata }: { metadata: ProcedureHeaderMetadata }) {
  return <DocumentHeader metadata={metadata} glossary />;
}

function ProcedureSectionView({ node }: NodeViewProps) {
  const metadata = useContext(ProcedureHeaderContext);
  const language = node.attrs.language === 'fr' ? 'fr' : 'en';
  const hidden = Boolean(metadata.language && metadata.language !== 'bilingual' && metadata.language !== language);
  return <NodeViewWrapper data-type="procedure-section" data-language={language} data-language-hidden={hidden ? 'true' : undefined} lang={language} style={hidden ? { display: 'none' } : undefined}>
    <DocumentHeader metadata={metadata} language={language} />
    <NodeViewContent className="procedure-section-content" />
  </NodeViewWrapper>;
}

export const ProcedureSection = Node.create({
  name: 'procedureSection', content: 'block+', defining: true, isolating: true,
  // Not part of the block group: sections cannot be nested or inserted inside tables/lists.
  addAttributes: () => ({ language: { default: 'en' } }),
  parseHTML: () => [{ tag: 'section[data-procedure-language]', getAttrs: (element) => ({ language: element.getAttribute('data-procedure-language') }) }],
  renderHTML: ({ node }) => ['section', { 'data-procedure-language': node.attrs.language, lang: node.attrs.language }, 0],
  addNodeView: () => ReactNodeViewRenderer(ProcedureSectionView),
});

function PageBreakView() {
  const { t } = useTranslation('qms');
  return <NodeViewWrapper data-type="page-break" contentEditable={false} className="not-prose my-8 flex select-none items-center gap-3 text-xs text-slate-400">
    <span className="flex-1 border-t border-dashed border-slate-300" /><span>{t('authoring.pageBreak')}</span><span className="flex-1 border-t border-dashed border-slate-300" />
  </NodeViewWrapper>;
}

export const ProcedurePageBreak = Node.create({
  name: 'pageBreak', group: 'block', atom: true, selectable: true,
  parseHTML: () => [{ tag: 'div[data-type="page-break"]' }],
  renderHTML: () => ['div', { 'data-type': 'page-break', style: 'break-before:page' }],
  addNodeView: () => ReactNodeViewRenderer(PageBreakView),
});
